import React, { useEffect, useState } from 'react';
import { Button, Card, Badge, Alert, Nav } from 'react-bootstrap';
import { getTripSettings, useAccountState, useScheduleState } from '../../../../Context';
import Loader from '../../../../Components/Loader';
import TimeOffRequestModal from '../../../../Components/ScheduleModals/TimeOffRequestModal';
import AvailabilityRequestModal from '../../../../Components/ScheduleModals/AvailabilityRequestModal';
import RequestDetailModal from '../../../../Components/ScheduleModals/RequestDetailModal';
import CancelConfirmModal from '../../../../Components/ScheduleModals/CancelConfirmModal';
import './index.scss';
import {
    cancelScheduleRequest,
    editScheduleRequest,
    fetchAbsenceTypes,
    fetchAvailabilityRequests,
    fetchScheduleRequests,
    submitAvailabilityRequest,
    submitScheduleRequest,
} from '../../../../Context/actions/scheduleActions';

const TIME_OFF_REQUEST_TYPE_ID = 1;
const AVAILABILITY_REQUEST_TYPE_ID = 2;

const STATUS_VARIANTS = {
    pending: 'warning',
    approved: 'success',
    denied: 'danger',
};

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MobSchedule = () => {
    const {
        scheduleDispatch,
        schedule: { scheduleRequests, availabilityRequests, absenceTypes, loading, errorMessage },
    } = useScheduleState();

    const {
        AccountsDispatch,
        tripSettings: { tripSettings },
    } = useAccountState();

    const [activeTab, setActiveTab] = useState('timeoff');

    // Modal state
    const [submitError, setSubmitError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Time Off modal
    const [showTimeOffModal, setShowTimeOffModal] = useState(false);
    const [timeOffMode, setTimeOffMode] = useState('add');
    const [timeOffEditData, setTimeOffEditData] = useState(null);

    // Availability modal
    const [showAvailModal, setShowAvailModal] = useState(false);
    const [availMode, setAvailMode] = useState('add');
    const [availEditData, setAvailEditData] = useState(null);

    // Detail modal
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // Cancel confirm
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    useEffect(() => {
        (async () => {
            await Promise.all([
                fetchAbsenceTypes(scheduleDispatch),
                fetchScheduleRequests(scheduleDispatch),
                fetchAvailabilityRequests(scheduleDispatch),
                getTripSettings(AccountsDispatch),
            ]);
        })();
    }, [scheduleDispatch, AccountsDispatch]);

    // ── Open Add Modals ──
    const openAddTimeOff = () => {
        setTimeOffMode('add');
        setTimeOffEditData(null);
        setSubmitError(null);
        setShowTimeOffModal(true);
    };

    const openAddAvailability = () => {
        setAvailMode('add');
        setAvailEditData(null);
        setSubmitError(null);
        setShowAvailModal(true);
    };

    // ── Detail Modal ──
    const openDetailModal = (item) => {
        setSelectedRequest(item);
        setSubmitError(null);
        setShowDetailModal(true);
    };

    const closeDetailModal = () => {
        setShowDetailModal(false);
        setSelectedRequest(null);
        setSubmitError(null);
    };

    // ── Edit from Detail ──
    const handleEditFromDetail = () => {
        const req = selectedRequest;
        closeDetailModal();
        if (req.requestTypeID === TIME_OFF_REQUEST_TYPE_ID) {
            setTimeOffMode('edit');
            setTimeOffEditData(req);
            setSubmitError(null);
            setShowTimeOffModal(true);
        } else {
            setAvailMode('edit');
            setAvailEditData(req);
            setSubmitError(null);
            setShowAvailModal(true);
        }
    };

    // ── Submit Handlers ──
    const handleTimeOffSubmit = async (data) => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            if (timeOffMode === 'edit' && timeOffEditData) {
                await editScheduleRequest(scheduleDispatch, timeOffEditData.id, data);
            } else {
                await submitScheduleRequest(scheduleDispatch, {
                    requestTypeID: TIME_OFF_REQUEST_TYPE_ID,
                    ...data,
                });
            }
            setShowTimeOffModal(false);
        } catch {
            setSubmitError(
                timeOffMode === 'edit' ? 'Failed to update request.' : 'Failed to submit request. Please try again.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleAvailSubmit = async (data) => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            if (availMode === 'edit' && availEditData) {
                await editScheduleRequest(scheduleDispatch, availEditData.id, data);
            } else {
                await submitAvailabilityRequest(scheduleDispatch, {
                    requestTypeID: AVAILABILITY_REQUEST_TYPE_ID,
                    ...data,
                });
            }
            setShowAvailModal(false);
        } catch {
            setSubmitError(
                availMode === 'edit'
                    ? 'Failed to update availability request.'
                    : 'Failed to submit availability request. Please try again.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelRequest = async () => {
        setSubmitting(true);
        setSubmitError(null);
        try {
            await cancelScheduleRequest(scheduleDispatch, selectedRequest.id);
            setShowCancelConfirm(false);
            closeDetailModal();
        } catch (err) {
            setSubmitError(err?.response?.data?.message || 'Failed to cancel request.');
            setShowCancelConfirm(false);
        } finally {
            setSubmitting(false);
        }
    };

    const currentRequests = activeTab === 'timeoff' ? scheduleRequests : availabilityRequests;
    const emptyMessage = activeTab === 'timeoff' ? 'No time off requests yet.' : 'No availability submissions yet.';

    return (
        <div className="mob-schedule-page px-3 py-3">
            <Nav variant="pills" activeKey={activeTab} onSelect={setActiveTab} className="mb-3 mob-schedule-tabs">
                <Nav.Item>
                    <Nav.Link eventKey="timeoff">Time Off</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="availability">Availability</Nav.Link>
                </Nav.Item>
            </Nav>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 mob-schedule-title">{activeTab === 'timeoff' ? 'Time Off' : 'Availability'}</h5>
                {!!tripSettings?.enableDriverScheduling &&
                    (activeTab === 'timeoff' ? (
                        <Button size="sm" className="mob-schedule-btn-primary" onClick={openAddTimeOff}>
                            + New Request
                        </Button>
                    ) : (
                        <Button size="sm" className="mob-schedule-btn-primary" onClick={openAddAvailability}>
                            + New Request
                        </Button>
                    ))}
            </div>

            {loading && <Loader />}
            {errorMessage && (
                <Alert variant="danger" className="py-2">
                    {errorMessage}
                </Alert>
            )}

            {!loading && currentRequests.length === 0 && <p className="text-muted text-center mt-4">{emptyMessage}</p>}

            {/* Time Off Cards */}
            {!loading &&
                activeTab === 'timeoff' &&
                scheduleRequests.map((req) => (
                    <Card
                        key={req.id}
                        className="mob-schedule-card mb-3 cursor-pointer"
                        onClick={() => openDetailModal(req)}
                    >
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="mob-schedule-dates">
                                    {req.startDate}
                                    {req.endDate !== req.startDate && ` – ${req.endDate}`}
                                </span>
                                <Badge variant={STATUS_VARIANTS[req.status] || 'secondary'}>
                                    {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                                </Badge>
                            </div>
                            <div className="mob-schedule-reason">{req.absenceType?.description || '—'}</div>
                            {req.isPartialDay && (
                                <div className="mob-schedule-partial text-muted">
                                    Partial: {req.partialDayStart} – {req.partialDayEnd}
                                </div>
                            )}
                            {req.notes && <div className="mob-schedule-notes text-muted">{req.notes}</div>}
                        </Card.Body>
                    </Card>
                ))}

            {/* Availability Cards */}
            {!loading &&
                activeTab === 'availability' &&
                availabilityRequests.map((req) => (
                    <Card
                        key={req.id}
                        className="mob-schedule-card mb-3 cursor-pointer"
                        onClick={() => openDetailModal(req)}
                    >
                        <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="mob-schedule-dates">
                                    {req.startDate} – {req.endDate}
                                </span>
                                <Badge variant={STATUS_VARIANTS[req.status] || 'secondary'}>
                                    {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                                </Badge>
                            </div>
                            {req.days && req.days.length > 0 && (
                                <div className="mob-schedule-reason">
                                    {req.days
                                        .slice()
                                        .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
                                        .map((d) => DAY_LABELS[d.dayOfWeek]?.slice(0, 3))
                                        .join(', ')}
                                </div>
                            )}
                            {req.days && req.days.length > 0 && (
                                <div className="mob-schedule-partial text-muted">
                                    {(() => {
                                        const sorted = req.days.slice().sort((a, b) => a.dayOfWeek - b.dayOfWeek);
                                        const allSame = sorted.every(
                                            (d) =>
                                                d.startTime === sorted[0].startTime && d.endTime === sorted[0].endTime
                                        );
                                        if (allSame) return `${sorted[0].startTime} – ${sorted[0].endTime}`;
                                        return sorted
                                            .map(
                                                (d) =>
                                                    `${DAY_LABELS[d.dayOfWeek]?.slice(0, 3)}: ${d.startTime}–${
                                                        d.endTime
                                                    }`
                                            )
                                            .join(', ');
                                    })()}
                                </div>
                            )}
                            {req.notes && <div className="mob-schedule-notes text-muted">{req.notes}</div>}
                        </Card.Body>
                    </Card>
                ))}

            <TimeOffRequestModal
                show={showTimeOffModal}
                onHide={() => setShowTimeOffModal(false)}
                mode={timeOffMode}
                editData={timeOffEditData}
                absenceTypes={absenceTypes}
                onSubmit={handleTimeOffSubmit}
                submitting={submitting}
                submitError={submitError}
            />

            <AvailabilityRequestModal
                show={showAvailModal}
                onHide={() => setShowAvailModal(false)}
                mode={availMode}
                editData={availEditData}
                onSubmit={handleAvailSubmit}
                submitting={submitting}
                submitError={submitError}
            />

            <RequestDetailModal
                show={showDetailModal}
                onHide={closeDetailModal}
                request={selectedRequest}
                onEdit={handleEditFromDetail}
                onCancelRequest={() => setShowCancelConfirm(true)}
                submitting={submitting}
                submitError={submitError}
            />

            <CancelConfirmModal
                show={showCancelConfirm}
                onHide={() => setShowCancelConfirm(false)}
                onConfirm={handleCancelRequest}
                submitting={submitting}
            />
        </div>
    );
};

export default MobSchedule;
